import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Wand2, List, Heading, Table, FileText, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface AutoFormatterProps {
  content: string;
  onFormat: (formattedContent: string) => void;
  isLoading?: boolean;
}

export const AutoFormatter = ({ content, onFormat, isLoading = false }: AutoFormatterProps) => {
  const formatMarkdown = (text: string): string => {
    let formatted = text;

    // Convert numbered items to markdown lists
    formatted = formatted.replace(/^(\d+)\.\s+(.+)$/gm, '$1. $2');

    // Convert bullet points to markdown
    formatted = formatted.replace(/^[-*]\s+(.+)$/gm, '- $1');

    // Detect and format headings (lines that are all caps or start with common heading patterns)
    formatted = formatted.replace(/^([A-Z][A-Z\s]{3,})$/gm, '## $1');
    formatted = formatted.replace(/^(Introduction|Overview|Background|Methods|Results|Discussion|Conclusion):/gim, '## $1');

    // Format medical terms in bold (common patterns)
    formatted = formatted.replace(/\b([A-Z]{2,})\b/g, '**$1**');

    return formatted;
  };

  const convertToTable = (text: string): string => {
    // Detect tab-separated or comma-separated data
    const lines = text.split('\n');
    const tableLines: string[] = [];
    let inTable = false;

    lines.forEach((line, index) => {
      const tabs = line.split('\t');
      const commas = line.split(',');

      if (tabs.length > 1 || commas.length > 1) {
        const cells = tabs.length > 1 ? tabs : commas;
        
        if (!inTable) {
          // Header row
          tableLines.push('| ' + cells.join(' | ') + ' |');
          tableLines.push('|' + cells.map(() => ' --- ').join('|') + '|');
          inTable = true;
        } else {
          // Data rows
          tableLines.push('| ' + cells.join(' | ') + ' |');
        }
      } else {
        if (inTable) {
          inTable = false;
        }
        tableLines.push(line);
      }
    });

    return tableLines.join('\n');
  };

  const addStructure = (text: string): string => {
    let structured = text;

    // If no headings exist, try to add them
    if (!/^#{1,3}\s+/m.test(structured)) {
      // Split into paragraphs
      const paragraphs = structured.split('\n\n');
      
      if (paragraphs.length > 3) {
        // Add section headers
        const sections = [];
        sections.push('## Overview\n\n' + paragraphs.slice(0, Math.ceil(paragraphs.length / 3)).join('\n\n'));
        sections.push('\n\n## Details\n\n' + paragraphs.slice(Math.ceil(paragraphs.length / 3), Math.ceil(2 * paragraphs.length / 3)).join('\n\n'));
        sections.push('\n\n## Summary\n\n' + paragraphs.slice(Math.ceil(2 * paragraphs.length / 3)).join('\n\n'));
        structured = sections.join('');
      }
    }

    return structured;
  };

  const formatMedicalTerms = (text: string): string => {
    let formatted = text;

    // Common medical abbreviations to uppercase
    const medicalAbbr = ['BP', 'HR', 'RR', 'O2', 'ECG', 'MRI', 'CT', 'IV', 'IM', 'SC', 'PO', 'PRN', 'bid', 'tid', 'qid'];
    medicalAbbr.forEach(abbr => {
      const regex = new RegExp(`\\b${abbr.toLowerCase()}\\b`, 'gi');
      formatted = formatted.replace(regex, abbr);
    });

    return formatted;
  };

  const handleQuickFormat = () => {
    let formatted = content;
    formatted = formatMarkdown(formatted);
    formatted = formatMedicalTerms(formatted);
    onFormat(formatted);
    toast({
      title: "Quick format applied",
      description: "Basic markdown formatting has been applied",
    });
  };

  const handleAddStructure = () => {
    let formatted = content;
    formatted = addStructure(formatted);
    onFormat(formatted);
    toast({
      title: "Structure added",
      description: "Section headings have been added",
    });
  };

  const handleConvertTables = () => {
    let formatted = content;
    formatted = convertToTable(formatted);
    onFormat(formatted);
    toast({
      title: "Tables formatted",
      description: "Detected data converted to markdown tables",
    });
  };

  const handleFullFormat = () => {
    let formatted = content;
    formatted = formatMarkdown(formatted);
    formatted = convertToTable(formatted);
    formatted = addStructure(formatted);
    formatted = formatMedicalTerms(formatted);
    onFormat(formatted);
    toast({
      title: "Full formatting applied",
      description: "All formatting improvements have been applied",
    });
  };

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Wand2 className="h-4 w-4 text-primary" />
          Auto-Formatting Tools
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleQuickFormat}
            disabled={isLoading || !content}
            className="flex items-center gap-2"
          >
            <FileText className="h-3 w-3" />
            Quick Format
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleAddStructure}
            disabled={isLoading || !content}
            className="flex items-center gap-2"
          >
            <Heading className="h-3 w-3" />
            Add Structure
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleConvertTables}
            disabled={isLoading || !content}
            className="flex items-center gap-2"
          >
            <Table className="h-3 w-3" />
            Convert Tables
          </Button>

          <Button
            variant="default"
            size="sm"
            onClick={handleFullFormat}
            disabled={isLoading || !content}
            className="flex items-center gap-2"
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Wand2 className="h-3 w-3" />
            )}
            Full Format
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-3">
          Auto-format converts plain text to markdown, adds headings, creates tables, and formats medical terminology.
        </p>
      </CardContent>
    </Card>
  );
};
